import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ReservationTaskChangeSummary } from '@/models/reservation/reservation-task'

type Props = {
  changeSummary: ReservationTaskChangeSummary
}

export const ReservationTaskChangeDetail: React.FC<Props> = ({ changeSummary }) => {
  const { t } = useTranslation()
  const valueOrDash = (value: string) => (value === '' ? '-' : value)

  return (
    <div css={containerStyle}>
      <div css={labelStyle}>{t('reservationTask.ColumnChangeSummary')}</div>
      <div css={bodyStyle}>
        <div css={summaryStyle}>{changeSummary.summary}</div>
        {changeSummary.changes.length > 0 && (
          <ul css={changeListStyle}>
            {changeSummary.changes.map((change, index) => (
              <li key={index} css={changeItemStyle}>
                {/* 値は文字列として描画するので、i18next の HTML エスケープは不要（掛けると「/」が &#x2F; と出る） */}
                {t(change.difference === '' ? 'reservationTask.ChangeLine' : 'reservationTask.ChangeLineWithDifference', {
                  label: change.label,
                  before: valueOrDash(change.before),
                  after: valueOrDash(change.after),
                  difference: change.difference,
                  interpolation: { escapeValue: false },
                })}
              </li>
            ))}
          </ul>
        )}
        {changeSummary.dailyChanges.length > 0 && (
          <table css={dailyTableStyle}>
            <colgroup>
              <col css={stayDateColumnStyle} />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th css={dailyThStyle}>{t('reservationTask.ColumnStayDate')}</th>
                <th css={dailyThStyle}>{t('reservationTask.DailyChangeBefore')}</th>
                <th css={dailyThStyle}>{t('reservationTask.DailyChangeAfter')}</th>
              </tr>
            </thead>
            <tbody>
              {changeSummary.dailyChanges.map(dailyChange => (
                <tr key={dailyChange.stayDate}>
                  <td css={dailyTdStyle}>{dayjs(dailyChange.stayDate).format('YYYY/M/D')}</td>
                  <td css={dailyTdStyle}>{valueOrDash(dailyChange.before)}</td>
                  <td css={dailyTdStyle}>{valueOrDash(dailyChange.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const containerStyle = css({
  display: 'flex',
  fontSize: 12,
  lineHeight: 1.8,
  color: '#272727',
})

const labelStyle = css({
  flexShrink: 0,
  width: 92,
  color: '#676767',
})

const bodyStyle = css({
  minWidth: 0,
  overflowWrap: 'anywhere',
})

const summaryStyle = css({
  whiteSpace: 'pre-wrap',
})

const changeListStyle = css({
  listStyle: 'none',
})

const changeItemStyle = css({
  '&::before': { content: '"•"' },
})

const dailyTableStyle = css({
  width: 700,
  maxWidth: '100%',
  marginTop: 16,
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  border: '1px solid #CCCCCC',
  borderRadius: 5,
  overflow: 'hidden',
  background: '#FFFFFF',
})

const stayDateColumnStyle = css({
  width: 120,
})

const dailyThStyle = css({
  padding: '4px 16px',
  background: '#F5F5F5',
  color: '#676767',
  fontWeight: 'normal',
  textAlign: 'left',
  '&:not(:first-of-type)': { borderLeft: '1px solid #CCCCCC' },
})

const dailyTdStyle = css({
  padding: '4px 16px',
  borderTop: '1px solid #CCCCCC',
  '&:not(:first-of-type)': { borderLeft: '1px solid #CCCCCC' },
})
